// Indian-locale number formatting helpers.
import { normalizeLocale, type Locale } from '../i18n/locales';
import { DATE_NAMES, type DateToken } from '../data/dateNames.generated';

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const inrWhole = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const plain = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

// ---------------------------------------------------------------------------
// F43 / F124 — display currency + locale.
// All amounts are STORED and reasoned about in INR (the source of truth). These
// helpers convert only for display, using a process-set reference rate and the
// user's chosen locale. Conversion never feeds back into stored values.
// ---------------------------------------------------------------------------
type DisplayCurrency = 'INR' | 'USD';

let displayCurrency: DisplayCurrency = 'INR';
let displayLocale: Locale = 'en-IN';
// INR -> currency multipliers; refreshed from GET /api/fx/rates at runtime.
let fxFromInr: Record<DisplayCurrency, number> = { INR: 1, USD: 0.012 };

export const setDisplayPrefs = (opts: { currency?: string; locale?: string; fx?: Record<string, number> }) => {
  if (opts.currency === 'INR' || opts.currency === 'USD') displayCurrency = opts.currency;
  // EVERY supported locale, resolved through the one shared normaliser — not a
  // hand-listed shortlist. The old guard accepted only en-IN / hi-IN / en-US, so a
  // Bengali/Tamil/Telugu/Marathi/Kannada account silently stayed on 'en-IN' and every
  // date printed English month names under a fully translated UI. normalizeLocale
  // round-trips the legacy 'en-US' to 'en-IN' (Conv-24: language, not region — a
  // startup migration already rewrites the stored value) and fails safe to English on
  // anything unrecognised, so this can never be widened by accident.
  if (opts.locale != null) displayLocale = normalizeLocale(opts.locale);
  if (opts.fx && typeof opts.fx.USD === 'number' && opts.fx.USD > 0) {
    fxFromInr = { INR: 1, USD: opts.fx.USD };
  }
};

// Pin the numbering system to Latin digits on any locale tag we hand to Intl.
// Money is formatted 'en-IN' everywhere (Latin digits), but bn-IN defaults to
// Bengali numerals and mr-IN to Devanagari — so an unpinned date would render
// "১ আগ, ২০২৬" on the same card as "₹1,23,456". No-op for en-IN / hi-IN / ta-IN /
// te-IN / kn-IN, so today's output is unchanged.
const latnDigits = (locale: string) => (locale.includes('-u-') ? locale : `${locale}-u-nu-latn`);

export const getDisplayCurrency = (): DisplayCurrency => displayCurrency;

// Convert an INR amount to the active display currency and format it. When the
// display currency is INR this is identical to the existing formatInr output.
// EXACT, WITH PAISE, EVERYWHERE (founder 2026-10-31, after 199 sim-night-24
// findings on "₹8.90 L beside ₹70,000.00"): an exact figure is more honest than a
// rounded one. `whole` is kept in the signature for the ~40 call sites but no longer
// rounds — do not re-add whole-rupee or L/Cr rounding to a displayed amount. Chart
// AXIS ticks (formatCompactInrOnly) are scale marks, not figures, and stay compact.
export const formatMoney = (inrValue: number, _whole = false): string => {
  if (displayCurrency === 'INR') return formatInr(inrValue);
  const converted = inrValue * (fxFromInr[displayCurrency] ?? 1);
  return new Intl.NumberFormat(latnDigits(displayLocale), {
    style: 'currency',
    currency: displayCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(converted);
};

export const formatInr = (value: number, _whole = false) => inr.format(value);

// ---------------------------------------------------------------------------
// THE BILLING-DOMAIN FORMATTER: integer paise in, ₹ out, NEVER converted.
//
// Step credits, vouchers and plan prices are amounts in the CURRENCY OF ACCOUNT.
// A ₹100 credit comes off a ₹ Razorpay charge; it is not ₹100 worth of something
// that can be restated in dollars. Rendering it through formatMoney did exactly
// that — under a USD display preference (a real Settings option, fx 0.012) a
// ₹100 credit became "$1.20", and Wellness's own `money()` then applied its
// whole-rupee no-decimals rule to the CONVERTED figure and printed "$1": a 17%
// understatement on the one surface whose comment promises the arithmetic stays
// checkable, beside a plan bullet still saying "take ₹100 off the next one".
//
// The whole/decimal rule is decided AFTER nothing (there is no conversion), so
// it can never disagree with the number it is describing: whole rupees print
// without decimals, anything else with two.
export const formatPaiseInr = (paise: number) => {
  const rupees = paise / 100;
  return (Number.isInteger(rupees) ? inrWhole : inr).format(rupees);
};

export const formatNumber = (value: number) => plain.format(value);

export const formatPct = (value: number, signed = true) =>
  `${signed && value > 0 ? '+' : ''}${value.toFixed(2)}%`;

export const formatSigned = (value: number) => `${value > 0 ? '+' : ''}${plain.format(value)}`;

// Market caps arrive in ₹ crore. Render as ₹X.XX L Cr above 1 lakh crore.
export const formatMarketCapCr = (cr: number | null) => {
  if (!cr) return '—';
  if (cr >= 100000) return `₹${(cr / 100000).toFixed(2)} L Cr`;
  return `₹${plain.format(Math.round(cr))} Cr`;
};

/**
 * Compact rupees that NEVER convert — for a figure that belongs to the exchange
 * rather than to the reader (a share price axis, an index level). Its sibling
 * `formatCompactInr` delegates to `formatMoney` whenever the display currency is
 * not INR, which is correct for the user's own money and wrong here: it put a
 * `$35.80` y-axis under a `₹2,983.00` header on the same quote card.
 */
export const formatCompactInrOnly = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1e7) return `₹${(value / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(value / 1e5).toFixed(2)}L`;
  // Conv-57 fix 3 — no western K on rupee figures: 1,000–99,999 prints in full
  // Indian grouping (the panel read ₹70.0K as a unit it had to translate).
  if (abs >= 1e3) return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)}`;
  return `₹${Math.round(value)}`;
};

export const formatCompactInr = (value: number) => {
  // F43: in a non-INR display currency, show the full converted amount — the Cr/L/K
  // abbreviations are INR-specific — so the currency choice takes effect here too and
  // no screen mixes currencies (e.g. Portfolio P&L via this vs totals via formatMoney).
  // Kept in step with mobile/src/core/format.ts.
  // Founder 2026-10-31: no more L / Cr on displayed figures — Home and Portfolio
  // state the same money the same exact way (₹3,28,44,328.00), paise included.
  // Kept as a name so call sites need not move; it is now formatMoney.
  return formatMoney(value);
};

export const formatVolume = (value: number | null) => {
  if (!value) return '—';
  if (value >= 1_00_00_000) return `${(value / 1_00_00_000).toFixed(2)} Cr`;
  if (value >= 1_00_000) return `${(value / 1_00_000).toFixed(2)} L`;
  return plain.format(value);
};

// The locale tag is a PARAMETER defaulting to the active display locale (read at
// call time, so a language switch takes effect immediately). It was hardcoded
// 'en-IN', which printed English month names — "12 Aug 2026" — inside an otherwise
// fully translated Hindi/Bengali/Tamil screen.
//
// C22 (2026-10-19): the month NAME no longer comes from the device. Browsers and
// phone engines ship cut-down locale data — Chromium prints as-IN as "Jun 15,
// 2026" and pa-IN as "2026 M06 15", and the founder's phone printed Malayalam
// dates with an English month — so names and order come from CLDR, generated
// into dateNames.generated.ts. Day and year are Latin digits, as before, in the
// reader's local time zone, as before.
const namesFor = (locale: string) => DATE_NAMES[normalizeLocale(locale.split('-u-')[0])] ?? DATE_NAMES['en-IN'];
const renderDate = (d: Date, tokens: DateToken[], months: string[]) => tokens.map((tok) => {
  if ('lit' in tok) return tok.lit;
  if (tok.f === 'day') return String(d.getDate());
  if (tok.f === 'month') return months[d.getMonth()];
  return String(d.getFullYear());
}).join('');
// An unreadable date prints a dash (it used to print the engine's English
// "Invalid Date", which no catalog can translate).
const invalid = (d: Date) => Number.isNaN(d.getTime());

export const formatDate = (iso: string, locale: string = displayLocale) => {
  const d = new Date(iso);
  if (invalid(d)) return '—';
  const n = namesFor(locale);
  return renderDate(d, n.dayMonthYear, n.monthsShort);
};

/** "June 2026" in the reader's language — month and year only. Reads the UTC
 *  month by default, because every caller passes a 'YYYY-MM' key built in UTC and
 *  a local read would show December for January west of Greenwich. */
export const formatMonthYear = (date: Date, locale: string = displayLocale, utc = true) => {
  if (invalid(date)) return '';
  const d = utc ? new Date(date.getUTCFullYear(), date.getUTCMonth(), 15) : date;
  const n = namesFor(locale);
  return renderDate(d, n.monthYear, n.monthsLong);
};

// Day+month only, for chart crosshairs and compact rows. Shares the Latin-digit pin
// with formatDate: without it a Bengali/Devanagari locale renders the DAY in native
// numerals right beside a ₹ amount that is pinned to Latin, so one tooltip shows two
// numbering systems.
export const formatShortDate = (iso: string, locale: string = displayLocale, utc = false) => {
  const d0 = new Date(iso);
  if (invalid(d0)) return '—';
  const d = utc ? new Date(d0.getUTCFullYear(), d0.getUTCMonth(), d0.getUTCDate()) : d0;
  const n = namesFor(locale);
  return renderDate(d, n.dayMonth, n.monthsShort);
};

// The time: English keeps the engine's 12-hour clock so its output is unchanged;
// every other language gets 24-hour digits built here (no AM/PM word — the other
// thing a cut-down locale prints in English — and no reliance on the engine
// honouring `hourCycle`).
const pad2 = (n: number) => String(n).padStart(2, '0');
const timeOf = (d: Date, locale: string) => (normalizeLocale(locale.split('-u-')[0]) === 'en-IN'
  ? d.toLocaleTimeString('en-IN-u-nu-latn', { hour: '2-digit', minute: '2-digit' })
  : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`);

export const formatDateTime = (iso: string, locale: string = displayLocale) => {
  const d = new Date(iso);
  if (invalid(d)) return '—';
  return `${formatShortDate(iso, locale)}, ${timeOf(d, locale)}`;
};

/** Date + time WITH SECONDS — for an "As of" stamp beside a price. Simulated
 *  prices move every 5 s, so a minute stamp let one holding show three values on
 *  three screens under the same "08:17 pm" (sim night 24); seconds show each
 *  figure was fetched at its own moment. */
export const formatDateTimeSeconds = (iso: string, locale: string = displayLocale) => {
  const d = new Date(iso);
  if (invalid(d)) return '—';
  const time = normalizeLocale(locale.split('-u-')[0]) === 'en-IN'
    ? d.toLocaleTimeString('en-IN-u-nu-latn', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  return `${formatShortDate(iso, locale)}, ${time}`;
};

/** Date WITH the year, then the time — for records that can be months old. */
export const formatDateYearTime = (iso: string, locale: string = displayLocale) => {
  const d = new Date(iso);
  if (invalid(d)) return '—';
  return `${formatDate(iso, locale)}, ${timeOf(d, locale)}`;
};

// t(englishSource, vars?) — structurally identical to the i18n TFn, declared here
// rather than imported so this module never reaches into src/i18n/index.tsx (which
// imports AuthContext, which imports this file).
type Translate = (en: string, vars?: Record<string, string | number>) => string;

// Default: render the English source with its {placeholder}s filled in — the same
// interpolation the real t() does, so timeAgo(iso) with no t is byte-identical to
// the string this function returned before it was made translatable.
const asEnglish: Translate = (en, vars) =>
  vars ? en.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : en;

// This module has no React context, so it cannot call useT(). Callers inside a
// component pass their own t: timeAgo(iso, t). The four English sources below are
// the translation keys.
export const timeAgo = (iso: string, t: Translate = asEnglish) => {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return t('just now');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('{n}m ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{n}h ago', { n: hours });
  return t('{n}d ago', { n: Math.floor(hours / 24) });
};
